/**
 * P3-15: Census —— 猫口面板（迁移自 js/village.js:3995-4653）。
 *
 * 包含：
 * - Government 区（leader 信息、晋升/卸职链接）
 * - 过滤条（trait / job / sort 三个 select）
 * - 猫口列表（最多 10 条，分页）
 * - 分页链接
 *
 * 状态持久化：旧版用 `Census.statics` 在 tab 切换间保持过滤/排序/分页状态。
 * 本实现用模块级 `censusState` 对象等效保持。
 *
 * 刷新语义：旧版 `update()` 只更新当前 page 的 records；需要重排/重过滤时
 * 设置 `needsRefresh`，下次 update 重新 `render()`。新实现每 tick 直接重算
 * 过滤/排序/分页（Village tab 激活时才渲染），语义等价且更简单。
 */
import { useCallback, useEffect, useState } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";

// ---------------------------------------------------------------------------
// 模块级持久状态（对齐旧 Census.statics）
// ---------------------------------------------------------------------------

const censusState = {
  filterTrait: "",
  filterJob: "",
  sortKittens: "",
  startKitten: 0,
};

/** 测试用：重置模块级状态 */
export function resetCensusState(): void {
  censusState.filterTrait = "";
  censusState.filterJob = "";
  censusState.sortKittens = "";
  censusState.startKitten = 0;
}

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

interface Trait {
  name: string;
  title: string;
}

interface Job {
  name: string;
  title: string;
  unlocked: boolean;
}

interface Kitten {
  name: string;
  surname?: string;
  age: number;
  trait: Trait;
  rank: number;
  job?: string;
  exp: number;
  skills: Record<string, number>;
  favorite: boolean;
  isLeader: boolean;
  engineerSpeciality?: string;
  color?: number;
  variety?: number;
}

interface CensusGame {
  village: {
    leader: Kitten | null;
    traits: Trait[];
    jobs: Job[];
    sim: {
      kittens: Kitten[];
      canPromote(kitten: Kitten): boolean;
      promote(kitten: Kitten): void;
      sortKittensByExp(): void;
      sortKittensByFavorite(): void;
      sortKittensByColor(): void;
      sortKittensByVariety(): void;
      getSkillsSorted(skills: Record<string, number>): { name: string; val: number }[];
      getSkillsSortedWithJob(skills: Record<string, number>, job: string): { name: string; val: number }[];
    };
    getJob(name: string): Job | null;
    getRankExp(rank: number): number;
    getLeaderBonus(rank: number): number;
    getSkillExpRange(value: number): [number, number];
    getValueModifierPerSkill(value: number): number;
    getStyledName(kitten: Kitten, isLeaderPanel?: boolean): string;
    makeLeader(kitten: Kitten): void;
    unassignJob(kitten: Kitten): void;
    updateResourceProduction(): void;
  };
  challenges: { isActive(name: string): boolean };
  workshop: { get(name: string): { researched: boolean }; getCraft(name: string): { label: string } };
  religion: { getPact(name: string): { val: number } };
  getEffect(name: string): number;
  getFeatureFlag(name: string): boolean;
  getDisplayValueExt(value: number | string, prefix?: boolean, _dummy?: boolean, precision?: number, postfix?: string): string;
  toDisplayDays(days: number): string;
  render(): void;
}

function asCensusGame(game: GamePage): CensusGame {
  return game as unknown as CensusGame;
}

// ---------------------------------------------------------------------------
// skill 等级文本（旧 village.js:5274-5291）
// ---------------------------------------------------------------------------

function skillToText(value: number): string {
  switch (true) {
    case value < 100:
      return t("village.skill.dabbling");
    case value < 500:
      return t("village.skill.novice");
    case value < 1200:
      return t("village.skill.adequate");
    case value < 2500:
      return t("village.skill.competent");
    case value < 5000:
      return t("village.skill.skilled");
    case value < 9000:
      return t("village.skill.proficient");
    default:
      return t("village.skill.master");
  }
}

// ---------------------------------------------------------------------------
// Government
// ---------------------------------------------------------------------------

function CensusGovernment({ game }: { game: CensusGame }) {
  const leader = game.village.leader;

  const promote = useCallback(() => {
    if (!leader) {
      return;
    }
    game.village.sim.promote(leader);
  }, [game, leader]);

  const unassignLeaderJob = useCallback(() => {
    if (!leader?.job) {
      return;
    }
    game.village.unassignJob(leader);
    game.render();
  }, [game, leader]);

  if (!leader) {
    return (
      <div className="currentGovernment">
        <div className="currentLeader">
          <span>{t("village.census.lbl.leader")}: </span>
          {t("village.census.lbl.noLeader")}
        </div>
      </div>
    );
  }

  const traitTitle =
    leader.trait.name === "none"
      ? t("village.census.trait.none")
      : `${leader.trait.title} (${t("village.bonus.desc." + leader.trait.name)}) [${t("village.census.rank")} ${leader.rank}]`;

  const leaderInfo = `${game.village.getStyledName(leader, true)}, ${traitTitle}`;

  const nextRank = Math.floor(game.village.getRankExp(leader.rank));
  let expInfo = `exp: ${game.getDisplayValueExt(leader.exp)}`;
  if (nextRank > leader.exp) {
    expInfo += ` (${Math.floor(100 * leader.exp / nextRank)}%)`;
  }

  let jobBonusInfo = "";
  if (leader.rank > 0) {
    jobBonusInfo = `${t("village.job.bonus")}: ×${game.village.getLeaderBonus(leader.rank).toFixed(1)}`;
    if (leader.job) {
      const job = game.village.getJob(leader.job);
      if (job) {
        jobBonusInfo += ` (${job.title})`;
      }
    }
  }

  const expToPromote = game.village.getRankExp(leader.rank);
  const goldToPromote = 25 * (leader.rank + 1);
  const canPromote = game.village.sim.canPromote(leader);

  return (
    <div className="currentGovernment" style={{ paddingBottom: "10px" }}>
      <div className="currentLeader">
        <span>{t("village.census.lbl.leader")}: </span>
        {leaderInfo}
      </div>
      <div className="currentLeader" style={{ marginBottom: "1lh" }}>{expInfo}</div>
      {jobBonusInfo && <div className="currentLeader">{jobBonusInfo}</div>}
      {canPromote && (
        <a href="#" onClick={(e) => { e.preventDefault(); promote(); }}>
          {t("village.census.leader.propmote", [game.getDisplayValueExt(expToPromote.toFixed()), String(goldToPromote)])}
        </a>
      )}
      {leader.job && (
        <a href="#" onClick={(e) => { e.preventDefault(); unassignLeaderJob(); }}>
          {t("village.btn.unassign")}
        </a>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

const SORT_OPTIONS = [
  { name: "exp", title: "village.census.sort.exp" },
  { name: "favorite", title: "village.census.sort.favorite" },
  { name: "color", title: "village.census.sort.color" },
  { name: "variety", title: "village.census.sort.variety" },
];

interface CensusFiltersProps {
  game: CensusGame;
  filterTrait: string;
  setFilterTrait: (value: string) => void;
  filterJob: string;
  setFilterJob: (value: string) => void;
  sortKittens: string;
  setSortKittens: (value: string) => void;
  setStartKitten: (value: number) => void;
  hasKittens: boolean;
}

function CensusFilters({
  game,
  filterTrait,
  setFilterTrait,
  filterJob,
  setFilterJob,
  sortKittens,
  setSortKittens,
  setStartKitten,
  hasKittens,
}: CensusFiltersProps) {
  const traits = game.village.traits;
  const jobs = game.village.jobs;

  const hasOurTraitBeenSeen = !filterTrait || traits.some((t) => t.name === filterTrait);
  useEffect(() => {
    if (!hasOurTraitBeenSeen) {
      setFilterTrait("");
    }
  }, [hasOurTraitBeenSeen, setFilterTrait]);

  const onTraitChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterTrait(e.target.value);
    setStartKitten(0);
  }, [setFilterTrait, setStartKitten]);

  const onJobChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterJob(e.target.value);
    setStartKitten(0);
  }, [setFilterJob, setStartKitten]);

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortKittens(e.target.value);
    setStartKitten(0);
  }, [setSortKittens, setStartKitten]);

  return (
    <div className="censusFilters" style={{ height: "24px" }}>
      <select style={{ float: "right" }} value={filterTrait} onChange={onTraitChange} disabled={!hasKittens}>
        <option value="">{t("village.trait.filter.all")}</option>
        {traits.map((trait) => (
          <option key={trait.name} value={trait.name}>{trait.title}</option>
        ))}
      </select>
      <select style={{ float: "right" }} value={filterJob} onChange={onJobChange} disabled={!hasKittens}>
        <option value="">{t("village.census.filter.all")}</option>
        {jobs.filter((job) => job.unlocked).map((job) => (
          <option key={job.name} value={job.name}>{job.title}</option>
        ))}
        <option value="unemployed">{t("village.census.filter.unemployed")}</option>
      </select>
      <select style={{ float: "right" }} value={sortKittens} onChange={onSortChange} disabled={!hasKittens}>
        {SORT_OPTIONS.map((option) => (
          <option key={option.name} value={option.name}>{t(option.title)}</option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Census Block
// ---------------------------------------------------------------------------

interface CensusBlockProps {
  game: CensusGame;
  kitten: Kitten;
  isAnarchy: boolean;
}

function CensusBlock({ game, kitten, isAnarchy }: CensusBlockProps) {
  const onPromote = useCallback(() => {
    game.village.sim.promote(kitten);
  }, [game, kitten]);

  const onFavorite = useCallback(() => {
    // eslint-disable-next-line react-hooks/immutability
    kitten.favorite = !kitten.favorite;
    game.render();
  }, [game, kitten]);

  const onLeader = useCallback(() => {
    game.village.makeLeader(kitten);
    game.render();
  }, [game, kitten]);

  const onUnassign = useCallback(() => {
    game.village.unassignJob(kitten);
    game.village.updateResourceProduction();
    game.render();
  }, [game, kitten]);

  const hideInfo =
    game.religion.getPact("fractured").val && game.getFeatureFlag("MAUSOLEUM_PACTS");

  const info =
    `<div class="info">${game.village.getStyledName(kitten)}, ` +
    `${hideInfo ? "???" : kitten.age} ${t("village.census.age")}, ` +
    `${hideInfo ? "???" : kitten.trait.title}` +
    (kitten.rank === 0 ? "" : ` (${t("village.census.rank")} ${kitten.rank})`) +
    "</div>";

  const skillsArr = kitten.job
    ? game.village.sim.getSkillsSortedWithJob(kitten.skills, kitten.job)
    : game.village.sim.getSkillsSorted(kitten.skills);

  let skillInfo = "";
  for (let j = 0; j < Math.min(skillsArr.length, 3); j++) {
    const skill = skillsArr[j];
    if (!skill.val) {
      break;
    }
    const skillExp = game.village.getSkillExpRange(skill.val);
    const prevExp = skillExp[0];
    const nextExp = skillExp[1];
    const expPercent = ((skill.val - prevExp) / (nextExp - prevExp)) * 100;

    let style = "";
    let bonus = "";
    if (skill.name === kitten.job) {
      style = "style='font-weight: bold'";
      let mod = game.village.getValueModifierPerSkill(kitten.skills[kitten.job]);
      if (mod > 0 && kitten.isLeader) {
        mod = game.village.getLeaderBonus(kitten.rank) * (mod + 1) - 1;
      }
      if (mod > 0 && !kitten.isLeader && game.village.leader) {
        mod =
          (game.village.getLeaderBonus(game.village.leader.rank) * game.getEffect("boostFromLeader") + 1) *
          (mod + 1) -
          1;
      }
      mod *= 100;
      bonus = mod > 0 ? ` +${mod.toFixed(0)}%` : "";
    }

    let craftInfo = "";
    if (kitten.job === "engineer" && j === 0) {
      craftInfo = `${t("village.census.crafting")} `;
      if (kitten.engineerSpeciality) {
        craftInfo += game.workshop.getCraft(kitten.engineerSpeciality).label;
      } else {
        craftInfo += t("village.census.crafting.nothing");
      }
    }

    skillInfo +=
      `<span class="skill" title="${skill.val.toFixed(2)}" ${style}>` +
      `${game.village.getJob(skill.name)?.title ?? skill.name}${bonus}` +
      ` (${skillToText(skill.val)} ${expPercent.toFixed()}%) ${craftInfo}` +
      "</span><br>";
  }

  const canPromote = game.village.sim.canPromote(kitten);
  const leaderClass = kitten.isLeader ? "simLeader" : "";
  const favoriteClass = kitten.favorite ? "simFavorite" : "";

  return (
    <div className={`census-block ${leaderClass} ${favoriteClass}`}>
      <div style={{ display: "inline-block" }}>
        <div dangerouslySetInnerHTML={{ __html: info + skillInfo }} />
      </div>
      <div className="links-container">
        <span
          className="btn modern promoteHref"
          style={{ visibility: canPromote ? "visible" : "hidden" }}
          title={t("village.census.btn.promote")}
          onClick={onPromote}
        >
          ^
        </span>
        <span
          className="btn modern favoriteHref"
          title={t("village.census.btn.favorite")}
          onClick={onFavorite}
          dangerouslySetInnerHTML={{ __html: kitten.favorite ? "&#9733;" : "&#9734;" }}
        />
        <span
          className="btn modern leaderHref"
          style={{ visibility: !isAnarchy ? "visible" : "hidden" }}
          title={t("village.census.btn.leader")}
          onClick={onLeader}
          dangerouslySetInnerHTML={{ __html: kitten.isLeader ? "&#9873;" : "&#9872;" }}
        />
        <a
          href="#"
          className="unassignHref"
          style={{ visibility: kitten.job ? "visible" : "hidden" }}
          onClick={(e) => { e.preventDefault(); onUnassign(); }}
        >
          {t("village.btn.unassign.job")}
        </a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

interface CensusPaginationProps {
  startKitten: number;
  setStartKitten: (value: number) => void;
  numFiltered: number;
}

function CensusPagination({ startKitten, setStartKitten, numFiltered }: CensusPaginationProps) {
  const pageNumber = Math.floor(startKitten / 10) + 1;
  const numPages = Math.ceil(numFiltered / 10);

  const goFirst = useCallback(() => setStartKitten(0), [setStartKitten]);
  const goPrevious = useCallback(() => {
    setStartKitten(Math.max(0, startKitten - 10));
  }, [setStartKitten, startKitten]);
  const goNext = useCallback(() => {
    if (numFiltered - 10 > startKitten) {
      setStartKitten(startKitten + 10);
    }
  }, [setStartKitten, startKitten, numFiltered]);
  const goLast = useCallback(() => {
    let last = numFiltered / 10;
    if (Math.floor(last) === last) {
      last -= 1;
    }
    setStartKitten(Math.floor(last) * 10);
  }, [setStartKitten, numFiltered]);

  return (
    <div style={{ display: "inline-block", width: "100%", textAlign: "center", margin: "0 auto" }}>
      <a
        href="#"
        className="firstHref"
        style={{ visibility: pageNumber > 1 ? "visible" : "hidden", float: "left" }}
        onClick={(e) => { e.preventDefault(); goFirst(); }}
      >
        {"<<"}
      </a>
      <a
        href="#"
        className="previousHref"
        style={{ visibility: pageNumber > 1 ? "visible" : "hidden", float: "left" }}
        onClick={(e) => { e.preventDefault(); goPrevious(); }}
      >
        {t("village.btn.previous")}
      </a>
      {t("village.census.page", [String(pageNumber), String(numPages)])}
      <a
        href="#"
        className="nextHref"
        style={{ visibility: pageNumber < numPages ? "visible" : "hidden", float: "right" }}
        onClick={(e) => { e.preventDefault(); goNext(); }}
      >
        {t("village.btn.next")}
      </a>
      <a
        href="#"
        className="lastHref"
        style={{ visibility: pageNumber < numPages ? "visible" : "hidden", float: "right" }}
        onClick={(e) => { e.preventDefault(); goLast(); }}
      >
        {">>"}
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 主组件
// ---------------------------------------------------------------------------

function applyFilters(
  kitten: Kitten,
  filterJob: string,
  filterTrait: string,
): boolean {
  if (filterJob) {
    if (filterJob === "unemployed") {
      if (kitten.job) {
        return false;
      }
    } else if (kitten.job !== filterJob) {
      return false;
    }
  }
  if (filterTrait && kitten.trait.name !== filterTrait) {
    return false;
  }
  return true;
}

function applySort(game: CensusGame, sort: string): void {
  if (!sort) {
    game.village.sim.sortKittensByExp();
    return;
  }
  switch (sort) {
    case "exp":
      game.village.sim.sortKittensByExp();
      break;
    case "favorite":
      game.village.sim.sortKittensByFavorite();
      break;
    case "color":
      game.village.sim.sortKittensByColor();
      break;
    case "variety":
      game.village.sim.sortKittensByVariety();
      break;
  }
}

export function Census() {
  // 每 tick 触发一次重渲染（对齐旧 update 语义）
  useGameTick((game) => game?.ticks ?? 0);

  const [filterTrait, setFilterTrait] = useState(censusState.filterTrait);
  const [filterJob, setFilterJob] = useState(censusState.filterJob);
  const [sortKittens, setSortKittens] = useState(censusState.sortKittens);
  const [startKitten, setStartKitten] = useState(censusState.startKitten);

  useEffect(() => { censusState.filterTrait = filterTrait; }, [filterTrait]);
  useEffect(() => { censusState.filterJob = filterJob; }, [filterJob]);
  useEffect(() => { censusState.sortKittens = sortKittens; }, [sortKittens]);
  useEffect(() => { censusState.startKitten = startKitten; }, [startKitten]);

  const game = getGame();
  if (!game) {
    return null;
  }
  const cg = asCensusGame(game);

  const isAnarchy = cg.challenges.isActive("anarchy");

  // 排序（会修改 sim.kittens 数组，与旧版一致）
  applySort(cg, sortKittens);

  const filtered = cg.village.sim.kittens.filter((kitten) =>
    applyFilters(kitten, filterJob, filterTrait),
  );

  const numFiltered = filtered.length;
  const maxStart = Math.max(0, Math.ceil(numFiltered / 10 - 1) * 10);
  const safeStart = Math.min(startKitten, maxStart);

  const pageItems = filtered.slice(safeStart, safeStart + 10);

  return (
    <div className="censusContent">
      {!isAnarchy && <CensusGovernment game={cg} />}
      <CensusFilters
        game={cg}
        filterTrait={filterTrait}
        setFilterTrait={setFilterTrait}
        filterJob={filterJob}
        setFilterJob={setFilterJob}
        sortKittens={sortKittens}
        setSortKittens={setSortKittens}
        setStartKitten={setStartKitten}
        hasKittens={cg.village.sim.kittens.length > 0}
      />
      {pageItems.map((kitten, i) => (
        <CensusBlock key={safeStart + i} game={cg} kitten={kitten} isAnarchy={isAnarchy} />
      ))}
      {pageItems.length === 0 && (
        <span
          style={{
            display: "inline-block",
            width: "100%",
            textAlign: "center",
            margin: "5px auto",
          }}
        >
          {t(
            cg.village.sim.kittens.length === 0
              ? "village.census.no.kittens"
              : "village.census.no.kittens.filtered",
          )}
        </span>
      )}
      {pageItems.length > 0 && (
        <CensusPagination
          startKitten={safeStart}
          setStartKitten={setStartKitten}
          numFiltered={numFiltered}
        />
      )}
    </div>
  );
}
